import { useState } from 'react'
import { ChevronLeft, Ticket, Check, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAppStore } from '../store/useAppStore'
import { api } from '../lib/api'

const BAHT = '\u0E3F'
const BULLET = '\u2022'

const T = {
  title:        '\u0E0B\u0E37\u0E49\u0E2D\u0E15\u0E31\u0E4B\u0E27',
  subtitle:     '\u0E40\u0E25\u0E37\u0E2D\u0E01\u0E41\u0E1E\u0E47\u0E04\u0E40\u0E01\u0E08\u0E17\u0E35\u0E48\u0E04\u0E38\u0E49\u0E21\u0E04\u0E48\u0E32',
  yourTickets:  '\u0E15\u0E31\u0E4B\u0E27\u0E04\u0E07\u0E40\u0E2B\u0E25\u0E37\u0E2D',
  tickets:      '\u0E15\u0E31\u0E4B\u0E27',
  baht:         '\u0E1A\u0E32\u0E17',
  popular:      '\u0E22\u0E2D\u0E14\u0E19\u0E34\u0E22\u0E21',
  bestValue:    '\u0E04\u0E38\u0E49\u0E21\u0E17\u0E35\u0E48\u0E2A\u0E38\u0E14',
  buyNow:       '\u0E0B\u0E37\u0E49\u0E2D\u0E40\u0E25\u0E22',
  saveText:     '\u0E1B\u0E23\u0E30\u0E2B\u0E22\u0E31\u0E14',
  perTicket:    '\u0E15\u0E48\u0E2D\u0E15\u0E31\u0E4B\u0E27',
  hint:         '\u0E15\u0E31\u0E4B\u0E27\u0E43\u0E0A\u0E49\u0E40\u0E1B\u0E34\u0E14\u0E14\u0E27\u0E07\u0E2B\u0E23\u0E37\u0E2D\u0E17\u0E33\u0E19\u0E32\u0E22\u0E1D\u0E31\u0E19\u0E44\u0E14\u0E49\u0E17\u0E31\u0E48\u0E27\u0E41\u0E2D\u0E1B',
  noExpire:     '\u0E15\u0E31\u0E4B\u0E27\u0E44\u0E21\u0E48\u0E21\u0E35\u0E27\u0E31\u0E19\u0E2B\u0E21\u0E14\u0E2D\u0E32\u0E22\u0E38',
  cardCommon:   '\u0E01\u0E32\u0E23\u0E4C\u0E14 Common 1 \u0E43\u0E1A',
  cardRare:     '\u0E01\u0E32\u0E23\u0E4C\u0E14 Rare 1 \u0E43\u0E1A',
}

interface Pkg {
  id: string
  amount: number
  tickets: number
  bonus?: string
  badge?: string
  highlight?: boolean
}

const PACKAGES: Pkg[] = [
  { id: 'basic',   amount: 9,  tickets: 1 },
  // TODO: เปิดเมื่อระบบการ์ดพร้อม
  // { id: 'value',   amount: 19, tickets: 2, bonus: T.cardCommon, badge: T.popular },
  // { id: 'premium', amount: 49, tickets: 6, bonus: T.cardRare,   badge: T.bestValue, highlight: true },
]

interface ProfileData {
  dream_quota?: { paid_tickets?: number }
}

export default function StorePage() {
  const navigate = useNavigate()
  const { lineUid } = useAppStore()
  const [selected, setSelected] = useState<string>('basic')

  const { data: profile } = useQuery<ProfileData>({
    queryKey: ['user', 'profile', lineUid],
    queryFn: () => api.user.profile(lineUid!) as Promise<ProfileData>,
    enabled: !!lineUid,
    staleTime: 0,
  })

  const tickets = profile?.dream_quota?.paid_tickets ?? 0

  function handleBuy(pkg: Pkg) {
    navigate(`/payment?amount=${pkg.amount}&package=${pkg.id}`)
  }

  return (
    <div className="flex flex-col min-h-dvh pb-6" style={{ background: 'var(--color-bg)' }}>

      <div className="flex items-center gap-3 px-4 pt-6 pb-3">
        <button onClick={() => navigate(-1)} style={{ color: 'var(--color-text-muted)' }}>
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>{T.title}</h1>
      </div>

      <div className="mx-4 mb-5 p-5 rounded-3xl flex items-center justify-between"
           style={{ background: 'var(--gradient-gold)' }}>
        <div>
          <p className="text-xs font-semibold text-white opacity-80 mb-1">{T.yourTickets}</p>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-white">{tickets}</span>
            <span className="text-sm text-white opacity-80">{T.tickets}</span>
          </div>
        </div>
        <Ticket size={48} color="#fff" style={{ opacity: 0.5 }} />
      </div>

      <p className="px-4 text-xs mb-3 text-center" style={{ color: 'var(--color-text-muted)' }}>
        {T.subtitle}
      </p>

      <div className="mx-4 flex flex-col gap-3">
        {PACKAGES.map(pkg => {
          const isSelected = selected === pkg.id
          const pricePerTicket = (pkg.amount / pkg.tickets).toFixed(1)
          const savePercent = Math.round((1 - parseFloat(pricePerTicket) / 9) * 100)

          return (
            <button
              key={pkg.id}
              onClick={() => setSelected(pkg.id)}
              className={`relative p-4 rounded-2xl text-left transition-all ${
                isSelected ? 'ring-2 scale-[1.02]' : 'opacity-90'
              }`}
              style={{
                background: pkg.highlight ? 'var(--gradient-gold)' : 'var(--color-surface)',
                border: '1px solid var(--color-surface-3)',
                ...(isSelected && {
                  boxShadow: '0 4px 20px rgba(184,148,90,0.3)',
                }),
              }}>

              {pkg.badge && (
                <div className="absolute -top-2 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold"
                     style={{
                       background: pkg.highlight ? '#fff' : 'var(--gradient-gold)',
                       color: pkg.highlight ? 'var(--color-gold-dark)' : '#fff',
                     }}>
                  {pkg.badge}
                </div>
              )}

              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-bold"
                          style={{ color: pkg.highlight ? '#fff' : 'var(--color-text)' }}>
                      {pkg.tickets}
                    </span>
                    <span className="text-xs font-medium"
                          style={{ color: pkg.highlight ? 'rgba(255,255,255,0.8)' : 'var(--color-text-muted)' }}>
                      {T.tickets}
                    </span>
                  </div>

                  {pkg.bonus && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <Sparkles size={11} color={pkg.highlight ? '#fff' : '#b8945a'} />
                      <span className="text-[11px]"
                            style={{ color: pkg.highlight ? 'rgba(255,255,255,0.9)' : 'var(--color-gold-dark)' }}>
                        + {pkg.bonus}
                      </span>
                    </div>
                  )}

                  <div className="text-[10px] mt-2"
                       style={{ color: pkg.highlight ? 'rgba(255,255,255,0.7)' : 'var(--color-text-soft)' }}>
                    {pricePerTicket} {T.baht} / {T.perTicket}
                    {savePercent > 0 && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded font-bold"
                            style={{
                              background: pkg.highlight ? 'rgba(255,255,255,0.2)' : 'rgba(56,161,105,0.15)',
                              color: pkg.highlight ? '#fff' : '#38a169',
                            }}>
                        {T.saveText} {savePercent}%
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1">
                  <div className="text-2xl font-bold"
                       style={{ color: pkg.highlight ? '#fff' : 'var(--color-gold-dark)' }}>
                    {pkg.amount}{BAHT}
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full flex items-center justify-center"
                         style={{ background: pkg.highlight ? '#fff' : 'var(--gradient-gold)' }}>
                      <Check size={14} color={pkg.highlight ? '#b8945a' : '#fff'} />
                    </div>
                  )}
                </div>
              </div>
            </button>
          )
        })}
      </div>

      <button onClick={() => {
                const pkg = PACKAGES.find(p => p.id === selected)
                if (pkg) handleBuy(pkg)
              }}
              className="mx-4 mt-6 py-4 rounded-2xl font-semibold btn-gold shimmer">
        {T.buyNow}
      </button>

      <div className="mx-4 mt-4 p-3 rounded-xl" style={{ background: 'var(--color-surface-2)' }}>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
          {BULLET} {T.hint}<br/>
          {BULLET} {T.noExpire}
        </p>
      </div>
    </div>
  )
}
